import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changesApi } from '../services/changesApi';
import { isChangeManager } from './changes/changePermissions';
import { labelChangeStatus } from './changes/changeLabels';
import { getApiErrorMessage } from '../utils/apiError';

interface BreakdownRow {
  count: number;
  others_grouped?: number;
}

interface DashboardPayload {
  operational: {
    total_changes: number;
    changes_by_type: Array<{ type: string; count: number }>;
    changes_by_status: Array<{ status: string; label?: string; count: number }>;
    changes_by_project: Array<BreakdownRow & { project: string | null }>;
    changes_by_category: Array<BreakdownRow & { category: string | null }>;
    changes_by_requester_area: Array<BreakdownRow & { requester_area: string | null }>;
    changes_by_system: Array<BreakdownRow & { system: string | null }>;
  };
  governance: {
    success_rate: number;
    rollback_count: number;
    emergency_changes: number;
    cancelled_changes: number;
    avg_approval_hours: number;
    avg_implementation_hours?: number;
  };
}

const formatDateInput = (value: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

/** Espelha o limite de janela de `backend/app/services/changes/dashboard.py`. */
const MAX_WINDOW_DAYS = 366;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const validateWindow = (dateFrom: string, dateTo: string): string | null => {
  if (!dateFrom || !dateTo) return null;
  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Informe datas válidas no filtro.';
  }
  if (end < start) {
    return 'A data final precisa ser maior ou igual à data inicial.';
  }
  if (Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) > MAX_WINDOW_DAYS) {
    return `Intervalo do dashboard limitado a ${MAX_WINDOW_DAYS} dias.`;
  }
  return null;
};

const BREAKDOWN_HINT = 'Top 8 por volume; o restante entra em Outros.';

export function breakdownLabel(name: string | null | undefined, othersGrouped?: number): string {
  const base = name && String(name).trim() ? String(name) : 'Não informado';
  if (othersGrouped && othersGrouped > 0) {
    return `${base} (${othersGrouped})`;
  }
  return base;
}

const ChangesDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDateInput(d);
  });
  const [dateTo, setDateTo] = useState(() => formatDateInput(new Date()));
  const canViewDashboard = isChangeManager(user);

  const loadIndicators = useCallback(async () => {
    const windowError = validateWindow(dateFrom, dateTo);
    if (windowError) {
      setError(windowError);
      setPayload(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await changesApi.dashboardIndicators({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setPayload(response as unknown as DashboardPayload);
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(
          err,
          (err as { message?: string })?.message || 'Não foi possível carregar os indicadores.',
        ),
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!canViewDashboard) return;
    void loadIndicators();
  }, [canViewDashboard, loadIndicators]);

  if (!canViewDashboard) {
    return <Navigate to="/changes" replace />;
  }

  return (
    <div className="container-fluid py-3">
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h4 className="mb-0">Dashboard de Mudanças</h4>
            <Button variant="outline-secondary" onClick={() => void loadIndicators()} disabled={loading}>
              Atualizar
            </Button>
          </div>
          <p className="small text-muted mb-3">
            Indicadores operacionais (volume e recortes) e governança no período filtrado.
          </p>
          <Row className="g-2">
            <Col xs={12} sm={6} md={3}>
              <Form.Label>De</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Label>Até</Form.Label>
              <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Col>
          </Row>
          {error ? (
            <Alert variant="danger" className="mt-3 mb-0 py-2" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col xs={6} md={2}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Total</div>
              <h3>{payload?.operational.total_changes ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Taxa de sucesso</div>
              <h3>{payload?.governance.success_rate ?? 0}%</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Rollbacks</div>
              <h3>{payload?.governance.rollback_count ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Emergenciais</div>
              <h3>{payload?.governance.emergency_changes ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card className="border-danger">
            <Card.Body>
              <div className="text-muted small">Canceladas</div>
              <h3 className="text-danger">{payload?.governance.cancelled_changes ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={2}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Média impl. (h)</div>
              <h3>{payload?.governance.avg_implementation_hours ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={12} md={6}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Tempo médio de aprovação (h)</div>
              <h3 className="mb-0">{payload?.governance.avg_approval_hours ?? 0}</h3>
              <div className="small text-muted mt-1">
                Baseado na última decisão APROVADO (decided_at) vs criação da mudança.
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mb-2 mt-1">Indicadores operacionais</h5>
      <Row className="g-3">
        <Col xs={12} md={6} lg={4}>
          <Card>
            <Card.Header>Por status</Card.Header>
            <Card.Body>
              <Table size="sm" responsive>
                <tbody>
                  {(payload?.operational.changes_by_status || []).map((row) => (
                    <tr key={row.status}>
                      <td>{row.label || labelChangeStatus(row.status)}</td>
                      <td className="text-end">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card>
            <Card.Header>Por tipo</Card.Header>
            <Card.Body>
              <Table size="sm" responsive>
                <tbody>
                  {(payload?.operational.changes_by_type || []).map((row) => (
                    <tr key={row.type}>
                      <td>{row.type}</td>
                      <td className="text-end">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card>
            <Card.Header>Por funcionalidade (categoria)</Card.Header>
            <Card.Body>
              <div className="small text-muted mb-2">{BREAKDOWN_HINT}</div>
              <Table size="sm" responsive>
                <tbody>
                  {(payload?.operational.changes_by_category || []).map((row) => (
                    <tr key={row.category || 'category'}>
                      <td>{breakdownLabel(row.category, row.others_grouped)}</td>
                      <td className="text-end">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card>
            <Card.Header>Por sistema (SAP / Não SAP)</Card.Header>
            <Card.Body>
              <div className="small text-muted mb-2">
                Recorte por classificação SAP configurada na mudança (substitui o antigo campo livre de
                sistema).
              </div>
              <Table size="sm" responsive>
                <tbody>
                  {(payload?.operational.changes_by_system || []).map((row) => (
                    <tr key={row.system || 'system'}>
                      <td>{breakdownLabel(row.system, row.others_grouped)}</td>
                      <td className="text-end">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card>
            <Card.Header>Por projeto</Card.Header>
            <Card.Body>
              <div className="small text-muted mb-2">{BREAKDOWN_HINT}</div>
              <Table size="sm" responsive>
                <tbody>
                  {(payload?.operational.changes_by_project || []).map((row) => (
                    <tr key={row.project || 'project'}>
                      <td>{breakdownLabel(row.project, row.others_grouped)}</td>
                      <td className="text-end">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card>
            <Card.Header>Por área solicitante</Card.Header>
            <Card.Body>
              <div className="small text-muted mb-2">{BREAKDOWN_HINT}</div>
              <Table size="sm" responsive>
                <tbody>
                  {(payload?.operational.changes_by_requester_area || []).map((row) => (
                    <tr key={row.requester_area || 'requester_area'}>
                      <td>{breakdownLabel(row.requester_area, row.others_grouped)}</td>
                      <td className="text-end">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ChangesDashboard;
